<template>
  <div class="dashboard">
    <div class="dashboard-header">
      <h1>FeedMaster Dashboard</h1>
      <p class="subtitle">Mirakl-Billbee Order Synchronisation</p>
    </div>

    <div class="grid grid-3" v-if="!appStore.loading">
      <!-- Statistics Cards -->
      <div class="card">
        <div class="card-header">
          <h3 class="card-title">Order Statistics</h3>
        </div>
        <div class="stats" v-if="statistics">
          <div class="stat-item">
            <div class="stat-number">{{ statistics.total_active }}</div>
            <div class="stat-label">Active Orders</div>
          </div>
          <div class="stat-item" v-if="statistics.total_shipped > 0">
            <div class="stat-number">{{ statistics.total_shipped }}</div>
            <div class="stat-label">Shipped</div>
          </div>
          <div class="stat-item">
            <div class="stat-number">{{ statistics.total_completed }}</div>
            <div class="stat-label">Completed</div>
          </div>
          <div class="stat-item" v-if="statistics.total_canceled > 0">
            <div class="stat-number">{{ statistics.total_canceled }}</div>
            <div class="stat-label">Canceled</div>
          </div>
        </div>
      </div>

      <!-- Orders to Accept -->
      <div class="card">
        <div class="card-header">
          <h3 class="card-title">Orders to Accept</h3>
          <div class="card-actions">
            <span class="badge badge-warning">{{ waitingOrders.length }}</span>
            <button @click="refreshOrders" :disabled="orderStore.loading" class="btn btn-outline btn-sm">
              Refresh
            </button>
          </div>
        </div>
        <div v-if="orderStore.loading" class="text-muted text-center py-3">
          Loading orders...
        </div>
        <div v-else-if="orderStore.error" class="alert alert-danger">
          {{ orderStore.error }}
        </div>
        <div class="orders-to-accept" v-else-if="waitingOrders && waitingOrders.length > 0">
          <div class="order-item" v-for="order in waitingOrders" :key="order.id">
            <div class="order-info">
              <div class="order-id">{{ order.id }}</div>
              <div class="order-details">
                <span class="customer">{{ order.customer_name || 'Unknown Customer' }}</span>
                <span class="amount">{{ formatCurrency(order.total_price) }}</span>
                <span class="date">{{ formatDate(order.created_date) }}</span>
              </div>
            </div>
            <div class="order-actions">
              <button @click="acceptOrder(order.id)" :disabled="processing" class="btn btn-success btn-sm">
                Accept
              </button>
              <button @click="rejectOrder(order.id)" :disabled="processing" class="btn btn-danger btn-sm">
                Reject
              </button>
            </div>
          </div>
        </div>
        <div v-else class="text-muted text-center py-3 empty-orders">
          <div>No orders waiting for acceptance</div>
          <small v-if="orderStore.lastFetch">Last checked {{ formatDateTime(orderStore.lastFetch) }}</small>
        </div>
      </div>

      <!-- Sync Status -->
      <div class="card">
        <div class="card-header">
          <h3 class="card-title">Sync Status</h3>
        </div>
        <div class="sync-summary" v-if="statistics && statistics.sync_status">
          <div class="sync-item">
            <span class="sync-label">Pending Billbee Sync:</span>
            <span class="sync-count">{{ statistics.sync_status.pending_billbee_sync }}</span>
          </div>
          <div class="sync-item">
            <span class="sync-label">Pending Shipment Update:</span>
            <span class="sync-count">{{ statistics.sync_status.pending_mirakl_shipment }}</span>
          </div>
          <div class="sync-item">
            <span class="sync-label">Pending Invoice Upload:</span>
            <span class="sync-count">{{ statistics.sync_status.pending_invoice_upload }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Recent Activity -->
    <div class="grid grid-2 mt-3">
      <div class="card">
        <div class="card-header">
          <h3 class="card-title">Last Run Times</h3>
        </div>
        <div class="last-run-list" v-if="lastRun">
          <div class="last-run-item" v-for="(time, task) in lastRun" :key="task">
            <span class="task-name">{{ formatTaskName(task) }}:</span>
            <span class="task-time">{{ formatDateTime(time) }}</span>
          </div>
        </div>
      </div>

      <div class="card">
        <div class="card-header">
          <h3 class="card-title">Recent Errors</h3>
        </div>
        <div class="error-list" v-if="recentErrors && recentErrors.length > 0">
          <div class="error-item" v-for="(error, index) in recentErrors.slice(0, 5)" :key="index">
            <div class="error-time">{{ formatDateTime(error.timestamp) }}</div>
            <div class="error-message">{{ error.message }}</div>
          </div>
        </div>
        <div v-else class="text-muted">No recent errors</div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="appStore.loading" class="loading">
      <div class="spinner"></div>
      <p>Loading dashboard...</p>
    </div>

    <!-- Error State -->
    <div v-if="appStore.error" class="alert alert-danger">
      {{ appStore.error }}
    </div>
  </div>
</template>

<script>
import { useAppStore } from '@/stores/appStore'
import { useOrderStore } from '@/stores/orderStore'
import { onMounted, computed, ref } from 'vue'

export default {
  name: 'Dashboard',
  setup() {
    const appStore = useAppStore()
    const orderStore = useOrderStore()
    const processing = ref(false)
    
    onMounted(async () => {
      await Promise.all([
        appStore.fetchStatus(),
        orderStore.fetchOrders()
      ])
    })
    
    const statistics = computed(() => appStore.status?.statistics || null)
    const lastRun = computed(() => appStore.status?.last_run || null)
    const recentErrors = computed(() => appStore.status?.recent_errors || [])
    const waitingOrders = computed(() => orderStore.waitingAcceptanceOrders || [])
    
    return {
      appStore,
      orderStore,
      statistics,
      lastRun,
      recentErrors,
      waitingOrders,
      processing
    }
  },
  
  methods: {
    async refreshOrders() {
      await this.orderStore.fetchOrders()
    },

    async acceptOrder(orderId) {
      if (this.processing) return
      
      if (!confirm('Accept this order?')) {
        return
      }
      
      try {
        this.processing = true
        await this.orderStore.acceptOrder(orderId)
        
        // Refresh both orders and statistics
        await Promise.all([
          this.orderStore.fetchOrders(),
          this.appStore.fetchStatus()
        ])
        
        alert('Order accepted successfully!')
        console.log(`Order ${orderId} accepted successfully`)
        
      } catch (error) {
        console.error('Failed to accept order:', error)
        alert('Failed to accept order: ' + (error.response?.data?.error || error.message || 'Unknown error'))
      } finally {
        this.processing = false
      }
    },
    
    async rejectOrder(orderId) {
      if (this.processing) return
      
      try {
        this.processing = true
        await this.orderStore.rejectOrder(orderId, 'Rejected from dashboard')
        
        // Refresh both orders and statistics
        await Promise.all([
          this.orderStore.fetchOrders(),
          this.appStore.fetchStatus()
        ])
        
        // Show success feedback
        console.log(`Order ${orderId} rejected successfully`)
        
      } catch (error) {
        console.error('Failed to reject order:', error)
        // Show error feedback
      } finally {
        this.processing = false
      }
    },
    
    formatTaskName(task) {
      const names = {
        'fetch_orders': 'Fetch Orders',
        'sync_orders': 'Sync Orders',
        'check_shipments': 'Check Shipments',
        'invoice_sync': 'Invoice Sync'
      }
      return names[task] || task
    },
    
    formatDateTime(dateString) {
      if (!dateString) return 'Never'
      return new Date(dateString).toLocaleString()
    },
    
    formatDate(dateString) {
      if (!dateString) return 'Unknown'
      return new Date(dateString).toLocaleDateString()
    },
    
    formatCurrency(amount) {
      if (!amount) return '€0.00'
      return new Intl.NumberFormat('de-DE', { 
        style: 'currency', 
        currency: 'EUR' 
      }).format(amount)
    },
    
    getStatusBadgeClass(status) {
      const classes = {
        'WAITING_ACCEPTANCE': 'badge-warning',
        'SHIPPING': 'badge-info',
        'SHIPPED': 'badge-success',
        'RECEIVED': 'badge-success',
        'CLOSED': 'badge-secondary',
        'COMPLETED': 'badge-success',
        'REFUSED': 'badge-danger',
        'CANCELED': 'badge-danger',
        'CANCELLED': 'badge-danger'
      }
      return classes[status] || 'badge-secondary'
    }
  }
}
</script>

<style scoped>
.dashboard-header {
  text-align: center;
  margin-bottom: 2rem;
}

.dashboard-header h1 {
  margin: 0;
  color: #333;
}

.subtitle {
  color: #6c757d;
  margin: 0.5rem 0 0 0;
}

.stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 1rem;
}

.stat-item {
  text-align: center;
}

.stat-number {
  font-size: 2rem;
  font-weight: bold;
  color: #0d6efd;
}

.stat-label {
  font-size: 0.875rem;
  color: #6c757d;
}

.status-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.status-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.count {
  font-weight: bold;
}

.sync-summary {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.sync-item {
  display: flex;
  justify-content: space-between;
}

.sync-label {
  font-size: 0.875rem;
}

.sync-count {
  font-weight: bold;
  color: #0d6efd;
}

.card-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.last-run-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.last-run-item {
  display: flex;
  justify-content: space-between;
  font-size: 0.875rem;
}

.task-name {
  font-weight: 500;
}

.task-time {
  color: #6c757d;
}

.error-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 300px;
  overflow-y: auto;
}

.error-item {
  padding: 0.5rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  border-left: 3px solid #dc3545;
}

.error-time {
  font-size: 0.75rem;
  color: #6c757d;
  margin-bottom: 0.25rem;
}

.error-message {
  font-size: 0.875rem;
}

/* Orders to Accept Styles */
.orders-to-accept {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 300px;
  overflow-y: auto;
}

.order-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background-color: #f8f9fa;
}

.order-info {
  flex: 1;
}

.order-id {
  font-weight: bold;
  color: #0d6efd;
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
}

.order-details {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  font-size: 0.75rem;
  color: #6c757d;
}

.order-details .customer {
  font-weight: 500;
  color: #495057;
}

.order-details .amount {
  font-weight: bold;
  color: #28a745;
}

.order-actions {
  display: flex;
  gap: 0.5rem;
  margin-left: 1rem;
}

.empty-orders {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.text-center {
  text-align: center;
}

.py-3 {
  padding-top: 1rem;
  padding-bottom: 1rem;
}
</style>